import {
  CacheEntities,
  fetchWithCache,
  generateCacheKey,
} from "@punchup/utils/caches";
import {
  type VenueWithLocations,
  getVenuePage,
  getVenuePageThemeWithFallback,
} from "../data/venue_pages";
import {
  applyPublicShowPipeline,
  getVenueWithLocations,
  publicShowStartCutoff,
} from "@punchup/data/server";

import {
  type GetVenueEmailFeaturedShowsDataResult,
  getVenueEmailFeaturedShowsData,
} from "./getVenueEmailFeaturedShowsData";
import {
  type GetVenueShowGroupDataResult,
  getVenueShowGroupData,
} from "./getVenueShowGroupData";

import type { ShowWithComedian } from "@punchup/data/legacy";
import type { VenuePageTimezones } from "@punchup/data/models";
import createNoAuthSupabaseClient from "@punchup/data/supabase/supabase";
import { getShowsForVenuePage } from "@punchup/data/legacy";

/**
 * Redis cache-aside for the public venue page's three hot reads. Every origin
 * read runs on the no-auth client so a shared entry is always the public view,
 * never a signed-in admin's; personalised reads stay on the callers' cookie
 * client. Origin retries are off: the origin is the database being protected.
 */

/** Shows fetched for the public venue page; the carousel shows 5, the list pages from here. */
export const VENUE_PAGE_SHOWS_PREFETCH_LIMIT = 20;

// The venue row's key is shared with the show page's getVenueWithLocationsWithCache — keep both TTLs in step.
const VENUE_PAGE_TTL_SECONDS = 60;
const VENUE_PAGE_THEME_TTL_SECONDS = 300;
const VENUE_PAGE_NAME_TTL_SECONDS = 300;
const VENUE_PAGE_SHOWS_TTL_SECONDS = 60;
// Also bounds how long a pre-send featured-set edit or a sold-out flip takes to show.
const VENUE_EMAIL_FEATURED_SHOWS_TTL_SECONDS = 60;
const VENUE_PAGE_SHOW_GROUP_TTL_SECONDS = 60;
const NO_RETRIES = { retries: 0 };

export function getCachedVenueWithLocations(
  slug: string
): Promise<VenueWithLocations> {
  // Same key + payload as the show page's getVenueWithLocationsWithCache, so
  // the two surfaces warm each other.
  return fetchWithCache(
    generateCacheKey(CacheEntities.VENUE_PAGE, slug),
    () => getVenueWithLocations(createNoAuthSupabaseClient(), { slug }),
    VENUE_PAGE_TTL_SECONDS,
    NO_RETRIES
  );
}

export function getCachedVenuePageTheme(
  slug: string
): ReturnType<typeof getVenuePageThemeWithFallback> {
  return fetchWithCache(
    generateCacheKey(CacheEntities.VENUE_PAGE_THEME, slug),
    () => getVenuePageThemeWithFallback(createNoAuthSupabaseClient(), slug),
    VENUE_PAGE_THEME_TTL_SECONDS,
    NO_RETRIES
  );
}

export function getCachedVenuePageName(slug: string): Promise<string> {
  return fetchWithCache(
    generateCacheKey(CacheEntities.VENUE_PAGE_NAME, slug),
    async () => {
      const venue = await getVenuePage(createNoAuthSupabaseClient(), { slug });
      return venue.name;
    },
    VENUE_PAGE_NAME_TTL_SECONDS,
    NO_RETRIES
  );
}

/**
 * Upcoming shows with the public pipeline applied (grace cutoff, live Tixologi
 * availability, password-protected/hidden events dropped). The cutoff moves
 * every second, so it is deliberately not part of the key.
 */
export function getCachedVenuePageShows(
  venuePageId: string,
  venueTimezones: VenuePageTimezones,
  options: { limit?: number } = {}
): Promise<ShowWithComedian[]> {
  return fetchWithCache(
    generateCacheKey(CacheEntities.VENUE_PAGE_SHOWS, venuePageId, {
      limit: options.limit ?? "all",
    }),
    async () => {
      const shows = await getShowsForVenuePage(
        createNoAuthSupabaseClient(),
        venuePageId,
        {
          startDate: publicShowStartCutoff(venueTimezones),
          limit: options.limit,
        }
      );
      return applyPublicShowPipeline(shows, venueTimezones);
    },
    VENUE_PAGE_SHOWS_TTL_SECONDS,
    NO_RETRIES
  );
}

/**
 * The email featured-shows landing page (`/shows/featured/[jobId]`) — the CTA
 * target of a venue email blast, so every recipient's click lands here within
 * minutes of the send. One entry per job + venue slug holds the fully
 * assembled page (job, shows, Tixologi events, live availability); a 404
 * result is cached too, so a bad link can't stampede the origin either.
 */
export function getCachedVenueEmailFeaturedShowsData(
  venueSlug: string,
  jobId: string
): Promise<GetVenueEmailFeaturedShowsDataResult> {
  return fetchWithCache(
    generateCacheKey(CacheEntities.VENUE_EMAIL_FEATURED_SHOWS, jobId, {
      slug: venueSlug,
    }),
    () =>
      getVenueEmailFeaturedShowsData(
        createNoAuthSupabaseClient(),
        venueSlug,
        jobId
      ),
    VENUE_EMAIL_FEATURED_SHOWS_TTL_SECONDS,
    NO_RETRIES
  );
}

/**
 * A show-group drill-down (resolved series + expanded Tixologi events + lineups
 * + sold-out overlay). Not-found and redirect results are cached too, so a
 * bogus group slug costs the origin once a minute, not once a request.
 */
export function getCachedVenueShowGroupData(
  venueSlug: string,
  groupSlug: string
): Promise<GetVenueShowGroupDataResult> {
  return fetchWithCache(
    generateCacheKey(CacheEntities.VENUE_PAGE_SHOW_GROUP, venueSlug, {
      group: groupSlug,
    }),
    () =>
      getVenueShowGroupData(createNoAuthSupabaseClient(), venueSlug, groupSlug),
    VENUE_PAGE_SHOW_GROUP_TTL_SECONDS,
    NO_RETRIES
  );
}
